import React from 'react';
import { Typography, Box, Paper, Stack, Grid, Card, CardContent, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { 
  Palette as PaletteIcon, 
  TextFields as TypographyIcon, 
  GridOn as SpacingIcon,
  ViewModule as ComponentsIcon,
  AccountBalance as FinancialIcon,
  Timeline as ProcessIcon
} from '@mui/icons-material';

const FeatureCard = ({ icon, title, description, link, color = 'primary' }) => {
  return (
    <Card 
      elevation={1} 
      sx={{ 
        height: '100%',
        border: '1px solid',
        borderColor: 'gray.g200',
        '&:hover': {
          borderColor: `${color}.main`,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          width: 48, 
          height: 48, 
          borderRadius: 2, 
          backgroundColor: `${color}.light`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2
        }}>
          <Box sx={{ color: `${color}.main`, fontSize: '1.5rem' }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="gray.g600" sx={{ mb: 3, flexGrow: 1 }}>
          {description}
        </Typography>
        <Button
          component={Link}
          to={link}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          Explore
        </Button>
      </CardContent>
    </Card>
  );
};

const Overview = () => {
  const features = [
    {
      icon: <PaletteIcon />,
      title: 'Colors',
      description: 'Comprehensive color palette with Autobia brand colors, semantic meanings, and accessibility guidelines.',
      link: '/colors',
      color: 'primary'
    },
    {
      icon: <TypographyIcon />,
      title: 'Typography',
      description: 'Typography system with Almarai font family, proper hierarchy, and responsive text sizes.',
      link: '/typography',
      color: 'secondary'
    },
    {
      icon: <SpacingIcon />,
      title: 'Spacing',
      description: '8px-based spacing system providing consistent rhythm and visual hierarchy across layouts.',
      link: '/spacing',
      color: 'success'
    },
    {
      icon: <ComponentsIcon />,
      title: 'Components',
      description: 'Reusable UI components extracted from the vouchers implementation with live examples.',
      link: '/inputs',
      color: 'primary'
    },
    {
      icon: <FinancialIcon />,
      title: 'Financial',
      description: 'Credit cards, enhanced stats, and payment components for financial interfaces.',
      link: '/financial',
      color: 'warning'
    },
    {
      icon: <ProcessIcon />,
      title: 'Process Flow',
      description: 'Steppers, advanced filters, and workflow components for complex processes.',
      link: '/process-flow',
      color: 'info'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ mb: 8 }}>
        <Stack spacing={3} sx={{ maxWidth: 800 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h4" color="white" fontWeight={700}>
                A
              </Typography>
            </Box>
            <Typography variant="h2" fontWeight={700} color="gray.g700">
              Autobia Design System
            </Typography>
          </Stack>
          
          <Typography variant="h5" color="gray.g600" fontWeight={400}>
            A comprehensive design system built from production components, 
            providing consistent UI patterns for Autobia's marketplace and payment infrastructure.
          </Typography>
          
         
        </Stack>
      </Box>

      {/* Features Grid */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h3" fontWeight={600} sx={{ mb: 4 }} color="gray.g700">
          Foundation & Components
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <FeatureCard {...feature} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Design Principles */}
      <Paper 
        sx={{ 
          p: 6, 
          backgroundColor: 'gray.g25',
          border: '1px solid',
          borderColor: 'gray.g200',
        }}
      >
        <Typography variant="h4" fontWeight={600} sx={{ mb: 4 }}>
          Design Principles
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Brand Consistency
                </Typography>
                <Typography variant="body1" color="gray.g600">
                  All components follow Autobia's brand identity with consistent use of 
                  orange and blue brand colors throughout the interface.
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Accessibility First
                </Typography>
                <Typography variant="body1" color="gray.g600">
                  WCAG AA compliant color contrasts and semantic HTML structure ensure 
                  the interface is accessible to all users.
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Responsive Design
                </Typography>
                <Typography variant="body1" color="gray.g600">
                  Mobile-first approach with comprehensive breakpoint system ensuring 
                  optimal experience across all device sizes.
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Developer Experience
                </Typography>
                <Typography variant="body1" color="gray.g600">
                  Built with Material-UI components and TypeScript support for enhanced 
                  developer productivity and code quality.
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Getting Started */}
      <Box sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, backgroundColor: 'primary.light', border: 'none' }}>
          <Typography variant="h4" fontWeight={600} sx={{ mb: 2 }}>
            Get Started
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: 600 }}>
            Explore the foundation elements like colors, typography, and spacing, 
            then dive into the component library to see interactive examples and usage guidelines.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              component={Link}
              to="/financial"
              variant="contained"
              color="warning"
              size="large"
            >
              Financial Components
            </Button>
            <Button
              component={Link}
              to="/process-flow"
              variant="contained"
              color="info"
              size="large"
            >
              Process Flow
            </Button>
            <Button
              component={Link}
              to="/colors"
              variant="outlined"
              color="primary"
              size="large"
            >
              Foundation
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default Overview;